import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { cn } from "@/lib/utils";
import { employeeService } from "@/services/employee.service";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getSecureJSON, setSecureJSON } from "../utils/localStorage";
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Building2,
  Settings,
  ChevronDown,
  ChevronRight,
  LogOut,
  UserPlus,
  Wallet,
  Package,
  ChevronLeft,
  User,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { motion, AnimatePresence } from "framer-motion";

interface SubMenuItem {
  title: string;
  href: string;
  module?: string;
  subModule?: string;
  action?: string;
}

interface MenuItem {
  title: string;
  icon: React.ReactNode;
  href: string;
  module?: string;
  subItems?: SubMenuItem[];
}

interface SidebarProps {
  onMobileClose?: () => void;
}

export default function Sidebar({ onMobileClose }: SidebarProps) {
  const { isAuthenticated, user, logout, hasModulePermission, hasSubModulePermission } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  // const { toast } = useToast(); - Removed as we use sonner directly
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentItems, setRecentItems] = useState<string[]>([]);
  const [userProfilePicture, setUserProfilePicture] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const menuItems: MenuItem[] = [
    {
      title: "Dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
      href: "/dashboard",
      module: "dashboard",
    },
    {
      title: "Employee Management",
      icon: <Users className="h-5 w-5" />,
      href: "/employees",
      module: "employee_management",
      subItems: [
        {
          title: "All Employees",
          href: "/employees",
          module: "employee_management",
          subModule: "employees",
          action: "view",
        },
        {
          title: "Departments",
          href: "/employees/departments",
          module: "employee_management",
          subModule: "department",
          action: "view",
        },
        {
          title: "Attendance",
          href: "/employees/attendance",
          module: "employee_management",
          subModule: "attendance",
          action: "view",
        },
        {
          title: "Performance",
          href: "/employees/performance",
          module: "employee_management",
          subModule: "performance",
          action: "view",
        },
        {
          title: "Leave Management",
          href: "/employees/leaves",
          module: "employee_management",
          subModule: "leave",
          action: "view",
        },
        {
          title: "Agreements",
          href: "/agreements",
          module: "employee_management",
          subModule: "agreement",
          action: "view",
        },
        {
          title: "Compensation",
          href: "/compensation",
          module: "employee_management",
          subModule: "compensation",
          action: "view",
        },
      ],
    },
    {
      title: "Hiring",
      icon: <UserPlus className="h-5 w-5" />,
      href: "/hiring",
      module: "hiring",
      subItems: [
        {
          title: "Job Postings",
          href: "/hiring/jobs",
          module: "hiring",
          subModule: "jobPosting",
          action: "view",
        },
        {
          title: "Candidates",
          href: "/hiring/candidates",
          module: "hiring",
          subModule: "candidate",
          action: "view",
        },
        {
          title: "Onboarding",
          href: "/hiring/onboarding",
          module: "hiring",
          subModule: "onboarding",
          action: "view",
        },
        {
          title: "Settings",
          href: "/hiring/settings",
          module: "hiring",
          subModule: "settings",
          action: "view",
        },
      ],
    },
    {
      title: "Projects",
      icon: <Briefcase className="h-5 w-5" />,
      href: "/projects",
      module: "project_management",
      subItems: [
        {
          title: "All Projects",
          href: "/projects",
          module: "project_management",
          subModule: "project",
          action: "view",
        },
        {
          title: "Tasks",
          href: "/projects/tasks",
          module: "project_management",
          subModule: "task",
          action: "view",
        },
        {
          title: "Settings",
          href: "/projects/settings",
          module: "project_management",
          subModule: "projectStatus",
          action: "view",
        },
        // {
        //   title: "Sprints",
        //   href: "/projects/sprints",
        //   module: "project_management",
        //   subModule: "sprint",
        //   action: "view",
        // },
        // {
        //   title: "Timeline",
        //   href: "/projects/timeline",
        //   module: "project_management",
        //   subModule: "timeline",
        //   action: "view",
        // },
        // {
        //   title: "Resources",
        //   href: "/projects/resources",
        //   module: "project_management",
        //   subModule: "resource",
        //   action: "view",
        // },
        // {
        //   title: "Deliverables",
        //   href: "/projects/deliverables",
        //   module: "project_management",
        //   subModule: "deliverable",
        //   action: "view",
        // },
      ],
    },
    {
      title: "Sales & Clients",
      icon: <Building2 className="h-5 w-5" />,
      href: "/sales",
      module: "sales",
      subItems: [
        {
          title: "Clients",
          href: "/sales/clients",
          module: "sales",
          subModule: "client",
          action: "view",
        },
        {
          title: "Proposals",
          href: "/sales/proposals",
          module: "sales",
          subModule: "proposal",
          action: "view",
        },
        {
          title: "Contracts",
          href: "/sales/contracts",
          module: "sales",
          subModule: "contract",
          action: "view",
        },
        {
          title: "Revenue",
          href: "/sales/revenue",
          module: "sales",
          subModule: "revenue",
          action: "view",
        },
      ],
    },
    {
      title: "Assets",
      icon: <Package className="h-5 w-5" />,
      href: "/assets",
      module: "assets",
      subItems: [
        {
          title: "All Assets",
          href: "/assets",
          module: "assets",
          subModule: "asset",
          action: "view",
        },
        {
          title: "Licenses",
          href: "/assets/licenses",
          module: "assets",
          subModule: "license",
          action: "view",
        },
        {
          title: "Maintenance",
          href: "/assets/maintenance",
          module: "assets",
          subModule: "maintenance",
          action: "view",
        },
      ],
    },
    {
      title: "Finance",
      icon: <Wallet className="h-5 w-5" />,
      href: "/finance",
      module: "finance",
      subItems: [
        {
          title: "Invoices",
          href: "/finance/invoices",
          module: "finance",
          subModule: "invoice",
          action: "view",
        },
        {
          title: "Expenses",
          href: "/finance/expenses",
          module: "finance",
          subModule: "expense",
          action: "view",
        },
        {
          title: "Payroll",
          href: "/finance/payroll",
          module: "finance",
          subModule: "payroll",
          action: "view",
        },
        {
          title: "Reports",
          href: "/finance/reports",
          module: "finance",
          subModule: "financialReport",
          action: "view",
        },
      ],
    },
    {
      title: "Settings",
      icon: <Settings className="h-5 w-5" />,
      href: "/settings",
      module: "settings",
      subItems: [
        {
          title: "Company Profile",
          href: "/settings/company-profile",
          module: "settings",
          subModule: "company",
          action: "view",
        },
        {
          title: "Roles & Permissions",
          href: "/settings/roles",
          module: "settings",
          subModule: "role",
          action: "view",
        },
        {
          title: "System Settings",
          href: "/settings/system",
          module: "settings",
          subModule: "jobTitle",
          action: "view",
        },
      ],
    },
  ];

  const hasViewPermission = React.useCallback((item: MenuItem | SubMenuItem): boolean => {
    if (!isAuthenticated || !user) return false;

    // Super admin check
    const role = user.role?.toLowerCase() || '';
    if (role.includes('super admin') || role.includes('super_admin')) return true;

    // Check for sub-item permissions
    if ('subModule' in item) {
      const sub = item as SubMenuItem;

      // Special case: Hiring Settings sub-item
      if (sub.title === 'Settings' && sub.module === 'hiring') {
        const hiringPerms = (user.permissions as any)?.hiring;
        const s = hiringPerms?.settings;
        return !!(s?.status?.view || s?.onboardingTask?.view);
      }

      // Default sub-module check: strictly check for 'view' permission
      if (sub.module && sub.subModule) {
        return hasSubModulePermission(sub.module, sub.subModule, sub.action || 'view');
      }
    }

    // Check for parent item permissions
    if (item.module) {
      if (item.module === 'dashboard') return true;

      // If it has sub-items, the filter in useMemo handles the parent visibility
      if ('subItems' in item && (item as any).subItems?.length > 0) {
        return true;
      }

      // Check module level permission
      return hasModulePermission(item.module, 'view');
    }

    return false;
  }, [isAuthenticated, user, hasModulePermission, hasSubModulePermission]);

  const filteredMenuItems = React.useMemo(() => {
    const normalizedRole = (user?.role || '').replace(/\s*\(Custom\)\s*$/i, '');
    const loweredRole = normalizedRole.toLowerCase();
    const isSuperAdmin = loweredRole === 'super admin' || loweredRole === 'super_admin';

    if (isSuperAdmin) {
      return menuItems;
    }

    return menuItems.map(item => {
      // Dashboard is always visible to everyone
      if (item.title === 'Dashboard') return item;

      const currentItem = { ...item };

      // Handle items with sub-items
      if (currentItem.subItems && currentItem.subItems.length > 0) {
        // Filter sub-items using the strictly-defined hasViewPermission
        let visibleSubItems = currentItem.subItems.filter(sub => hasViewPermission(sub));

        // Custom filtering for Finance: hide Payroll if it's there
        if (currentItem.title === 'Finance') {
          visibleSubItems = visibleSubItems.filter(sub => sub.title !== 'Payroll');
        }

        // If any sub-items are visible, show the parent with its visible sub-items
        if (visibleSubItems.length > 0) {
          return { ...currentItem, subItems: visibleSubItems };
        }

        // If no sub-items visible, don't show the parent
        return null;
      }

      // Handle items without sub-items (direct links)
      return hasViewPermission(currentItem) ? currentItem : null;
    }).filter((item): item is MenuItem => item !== null);
  }, [user, hasModulePermission, hasSubModulePermission, hasViewPermission]);

  useEffect(() => {
    const savedRecent = getSecureJSON("sidebarRecent");
    if (savedRecent) setRecentItems(savedRecent);

    // Refresh user permissions on mount to ensure sidebar is up to date
    if (isAuthenticated) {
      const { refreshUserPermissions } = useAuthStore.getState();
      refreshUserPermissions().catch(console.error);
    }
  }, [isAuthenticated]);

  // Fetch user's profile picture
  useEffect(() => {
    const fetchUserProfilePicture = async () => {
      if (user?.id) {
        try {
          const profileData = await employeeService.getMyProfile();
          if (profileData?.profilePicture) {
            setUserProfilePicture(profileData.profilePicture);
          } else {
            setUserProfilePicture(null);
          }
        } catch (error) {
          // If user doesn't have an employee profile (like Super Admin), set to null
          setUserProfilePicture(null);
        }
      }
    };

    fetchUserProfilePicture();
  }, [user?.id]);

  useEffect(() => {
    setSecureJSON("sidebarRecent", recentItems);
  }, [recentItems]);

  useEffect(() => {
    const currentPath = location.pathname;
    const expanded = menuItems
      .filter((item) => {
        // For parent menu items, check if current path starts with their href
        // For sub-items, check exact match
        if (item.subItems) {
          return item.subItems.some(subItem => currentPath === subItem.href);
        }
        return currentPath === item.href;
      })
      .map((item) => item.title);

    setExpandedItems(expanded);
  }, [location.pathname]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "/") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const toggleItem = (title: string) => {
    setExpandedItems((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  };

  const handleNavigation = (item: MenuItem | SubMenuItem) => {
    const { href } = item;

    console.log('🔍 Navigation clicked:', {
      title: item.title,
      href,
      hasPermission: hasViewPermission(item),
      module: (item as any).module,
      subModule: (item as any).subModule,
      action: (item as any).action
    });

    if (!hasViewPermission(item)) {
      console.error('❌ Permission denied for:', item.title);
      toast.error("Access Denied", {
        description: "You do not have permission to access this page.",
      });
      return;
    }

    console.log('✅ Navigating to:', href);

    try {
      navigate(href);
      console.log('✅ Navigation successful to:', href);
    } catch (error) {
      console.error('❌ Navigation error:', error);
    }

    onMobileClose?.();

    setRecentItems((prev) => {
      const newItems = [href, ...prev.filter((p) => p !== href)];
      return [...new Set(newItems)].slice(0, 5);
    });
  };

  const menuToRender = React.useMemo(() => {
    if (!searchQuery) {
      return filteredMenuItems;
    }

    const searchLower = searchQuery.toLowerCase();

    return filteredMenuItems
      .map((item) => {
        const selfMatch = item.title.toLowerCase().includes(searchLower);

        if (item.subItems) {
          if (selfMatch) {
            return item;
          }

          const matchingSubItems = item.subItems.filter((subItem) =>
            subItem.title.toLowerCase().includes(searchLower)
          );

          if (matchingSubItems.length > 0) {
            return { ...item, subItems: matchingSubItems };
          }
        } else if (selfMatch) {
          return item;
        }

        return null;
      })
      .filter((item): item is MenuItem => item !== null);
  }, [searchQuery, filteredMenuItems]);

  const renderMenuItem = (item: MenuItem) => {
    const isOpen = expandedItems.includes(item.title);
    const isActive = item.subItems
      ? item.subItems.some(subItem => location.pathname === subItem.href)
      : (location.pathname === item.href || (item.href === "/dashboard" && (location.pathname === "/" || location.pathname === "/dashboard")));

    if (item.subItems) {
      // Filter sub-items based on individual permissions
      const visibleSubItems = item.subItems.filter(hasViewPermission);

      if (visibleSubItems.length === 0) {
        return null;
      }

      return (
        <motion.div
          key={item.title}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-1"
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    "w-full justify-start rounded-xl px-3 py-2 text-sm font-medium transition-all",
                    "hover:bg-blue-50/50 hover:text-blue-600 hover:shadow-sm",
                    isActive && "bg-blue-100 text-blue-700 font-semibold",
                    !item.subItems && "h-10"
                  )}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    toggleItem(item.title);
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 shadow-sm">
                        {item.icon}
                      </div>
                      {!isCollapsed && <span>{item.title}</span>}
                    </div>
                    {!isCollapsed && item.subItems && (
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 transition-transform duration-200",
                          isOpen ? "transform rotate-180" : ""
                        )}
                      />
                    )}
                  </div>
                </Button>
              </TooltipTrigger>
              {isCollapsed && (
                <TooltipContent side="right" className="bg-white">
                  {item.title}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          {!isCollapsed && item.subItems && (
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="pl-4 space-y-1">
                    {visibleSubItems.map((subItem) => {
                      const isSubActive = location.pathname === subItem.href;
                      return (
                        <motion.div
                          key={subItem.title}
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          exit={{ x: -20, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            className={cn(
                              "w-full justify-start rounded-xl px-3 py-2 text-sm font-medium transition-all",
                              "hover:bg-blue-50/50 hover:text-blue-600 hover:shadow-sm",
                              isSubActive && "bg-blue-100 text-blue-700 font-semibold"
                            )}
                            onClick={(e: React.MouseEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleNavigation(subItem);
                            }}
                          >
                            {subItem.title}
                          </Button>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </motion.div>
      );
    }

    if (!hasViewPermission(item)) {
      return null;
    }

    return (
      <motion.div
        key={item.title}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="space-y-1"
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className={cn(
                  "w-full justify-start rounded-xl px-3 py-2 text-sm font-medium transition-all",
                  "hover:bg-blue-50/50 hover:text-blue-600 hover:shadow-sm",
                  isActive && "bg-blue-100 text-blue-700 font-semibold"
                )}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNavigation(item);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 shadow-sm">
                    {item.icon}
                  </div>
                  {!isCollapsed && <span>{item.title}</span>}
                </div>
              </Button>
            </TooltipTrigger>
            {isCollapsed && (
              <TooltipContent side="right" className="bg-white">
                {item.title}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </motion.div>
    );
  };

  return (
    <div
      className={cn(
        "h-full bg-white border-r border-gray-200/80 flex flex-col transition-all duration-300",
        isCollapsed ? "w-[80px]" : "w-[280px]"
      )}
    >
      <div className="p-4 border-b border-gray-200/80">
        <div className="flex items-center gap-2">
          {!isCollapsed && (
            <Input
              ref={searchInputRef}
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9"
            />
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="h-9 w-9"
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-1">
          {menuToRender.map((item) => renderMenuItem(item))}
        </div>
      </div>

      <div className="border-t border-gray-200/80 p-4">
        <div
          className={cn(
            "rounded-xl bg-gradient-to-r from-white/50 to-blue-50/30 p-3 shadow-sm",
            isCollapsed
              ? "flex flex-col items-center gap-2"
              : "flex items-center gap-3"
          )}
        >
          <div
            className={cn(
              "flex items-center gap-2",
              isCollapsed ? "flex-col" : "flex-1"
            )}
          >
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-sm">
              {userProfilePicture ? (
                <img
                  src={userProfilePicture.startsWith('http')
                    ? userProfilePicture
                    : `${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:8000'}${userProfilePicture}`}
                  alt={`${user?.firstName || ''} ${user?.lastName || ''}`}
                  className="h-8 w-8 rounded-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : (
                <User className="h-4 w-4 text-blue-600" />
              )}
            </div>
            {!isCollapsed && (
              <div>
                <div className="font-medium text-slate-900">{user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email}</div>
                <div className="text-xs text-blue-600/70">
                  {user?.role?.replace(/\s*\(Custom\)\s*$/i, '') || 'Employee'}
                </div>
              </div>
            )}
          </div>
          <div
            className={cn(
              "flex items-center gap-2",
              isCollapsed ? "flex-col" : ""
            )}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate("/profile")}
                    className={cn(
                      "h-8 w-8 rounded-lg transition-all",
                      "hover:bg-blue-50/50 hover:text-blue-600",
                      location.pathname === "/profile" && "bg-blue-100 text-blue-700"
                    )}
                  >
                    <User className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-white">
                  <p>Profile</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={logout}
                    className="h-8 w-8 rounded-lg hover:bg-blue-50/50 hover:text-blue-600"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-white">
                  <p>Logout</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
}

